import { useState } from 'react';

export function JoinWaitlistPage() {
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !termsAccepted) return;

    setStatus('submitting');
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
      const res = await fetch(`${apiBase}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, terms_accepted: true }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || "You're on the list!");
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <div className="max-w-xl mx-auto flex items-center gap-2">
          <img src="/logo.svg" alt="Mazo" className="w-7 h-7 brightness-0 invert" />
          <span style={{ fontFamily: "'Outfit', sans-serif" }} className="font-medium text-xl text-white">
            Mazo
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="max-w-xl w-full">
          {status === 'success' ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-3xl font-bold text-white mb-3">
                You're on the list!
              </h1>
              <p className="text-gray-400 text-lg">
                {message}
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  Turn your meetings into<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#512FEB]">
                    LinkedIn posts
                  </span>
                </h1>
                <p className="text-gray-400 text-lg max-w-md mx-auto">
                  Mazo uses AI to transform your conversations, notes, and meetings into posts that sound like you.
                </p>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {[
                  { step: '1', title: 'Connect', desc: 'Link your meeting tools & notes' },
                  { step: '2', title: 'Extract', desc: 'AI finds key insights automatically' },
                  { step: '3', title: 'Publish', desc: 'Get ready-to-post LinkedIn content' },
                ].map((f) => (
                  <div key={f.step} className="text-center">
                    <div className="w-8 h-8 rounded-full bg-[#512FEB]/20 text-[#8B5CF6] text-sm font-bold flex items-center justify-center mx-auto mb-2">
                      {f.step}
                    </div>
                    <p className="text-white text-sm font-medium">{f.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#512FEB] focus:border-transparent text-base"
                    required
                    disabled={status === 'submitting'}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#512FEB] focus:ring-[#512FEB]"
                    disabled={status === 'submitting'}
                  />
                  <span className="text-sm text-gray-400">
                    I agree to the{' '}
                    <a href="https://getmazo.ai/terms" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:text-[#a78bfa] underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="https://getmazo.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:text-[#a78bfa] underline">
                      Privacy Policy
                    </a>
                  </span>
                </label>

                {status === 'error' && (
                  <p className="text-red-400 text-sm">{message}</p>
                )}

                <button
                  type="submit"
                  disabled={!email || !termsAccepted || status === 'submitting'}
                  className="w-full py-3.5 bg-[#512FEB] hover:bg-[#4525d4] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-base"
                >
                  {status === 'submitting' ? 'Joining...' : 'Join the Waitlist'}
                </button>
              </form>

              <p className="text-center text-gray-600 text-xs mt-6">
                We'll only use your email to send you an invitation. No spam, ever.
              </p>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-gray-600 text-xs">
          &copy; {new Date().getFullYear()} Mazo. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
